import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import {
  Zap, MessageSquare, BarChart3, BookOpen, ShoppingCart,
  Shield, Heart, HelpCircle, Play, Link2, Loader2, Clock, CheckCircle2,
  FlaskConical, Activity, Sparkles, Construction, Search,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SkillAnalyzerPanel from "@/components/skills/SkillAnalyzerPanel";
import ABTestPanel from "@/components/skills/ABTestPanel";
import { SkillFormDialog } from "@/components/skills/SkillFormDialog";
import { ChainBuilder } from "@/components/skills/ChainBuilder";
import { ThresholdPlayground } from "@/components/skills/ThresholdPlayground";
import { Breadcrumb } from "@/components/layout/Breadcrumb";
import { useSkills, useToggleSkill, useSkillFeedbackStats, type SkillRow } from "@/hooks/useSkillOS";
import { Pencil } from "lucide-react";

const skillMeta: Record<string, { icon: React.ReactNode; color: string }> = {
  conversational: { icon: <MessageSquare className="w-5 h-5" />, color: "text-primary" },
  summary: { icon: <BookOpen className="w-5 h-5" />, color: "text-blue-500" },
  analyzer: { icon: <BarChart3 className="w-5 h-5" />, color: "text-amber-500" },
  "knowledge-sync": { icon: <Link2 className="w-5 h-5" />, color: "text-emerald-500" },
  "sales-assistant": { icon: <ShoppingCart className="w-5 h-5" />, color: "text-purple-500" },
  sentiment: { icon: <Heart className="w-5 h-5" />, color: "text-rose-500" },
  moderator: { icon: <Shield className="w-5 h-5" />, color: "text-orange-500" },
  "faq-responder": { icon: <HelpCircle className="w-5 h-5" />, color: "text-cyan-500" },
};

export default function Skills() {
  const { toast } = useToast();
  const { data: skills = [], isLoading } = useSkills();
  const { data: feedbackStats = [] } = useSkillFeedbackStats();
  const toggle = useToggleSkill();
  const [editSkill, setEditSkill] = useState<SkillRow | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "paused" | "training">("all");

  const filteredSkills = skills.filter((s) => {
    if (search && !s.name.toLowerCase().includes(search.toLowerCase()) && !(s.description ?? "").toLowerCase().includes(search.toLowerCase())) return false;
    const q = s.quality_score ?? 0;
    if (statusFilter === "active" && !s.active) return false;
    if (statusFilter === "paused" && s.active) return false;
    if (statusFilter === "training" && q >= 70) return false;
    return true;
  });

  const handleToggle = (id: string, active: boolean, quality: number) => {
    // A9-03 publish gate: block enabling a skill whose quality_score < 70
    if (active && quality < 70) {
      toast({
        title: "Publish bloqueado",
        description: `Skill com quality_score=${quality}/100 (mín. 70). Rode mais testes no Lab antes de ativar.`,
        variant: "destructive",
      });
      return;
    }
    toggle.mutate({ id, active }, { onSuccess: () => toast({ title: "Skill atualizada" }) });
  };

  const getStats = (name: string) => feedbackStats.find((s) => s.name === name);
  const openCreate = () => { setEditSkill(null); setFormOpen(true); };
  const openEdit = (s: SkillRow) => { setEditSkill(s); setFormOpen(true); };

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: "Skills" }]} />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-headline">Skills Engine v3</h1>
          <p className="text-muted-foreground text-sm">Gerencie, analise, teste A/B e otimize as habilidades da SYRA.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/skills/observability"><Button variant="outline"><Activity className="w-4 h-4 mr-1" /> Observability</Button></Link>
          <Link to="/skills/lab"><Button variant="outline"><FlaskConical className="w-4 h-4 mr-1" /> Skill Lab</Button></Link>
          <Button onClick={openCreate}><FlaskConical className="w-4 h-4 mr-1" /> Nova Skill</Button>
        </div>
      </div>

      <SkillFormDialog open={formOpen} onOpenChange={setFormOpen} skill={editSkill} />

      <Tabs defaultValue="registry" className="space-y-4">
        <TabsList>
          <TabsTrigger value="registry">Registro</TabsTrigger>
          <TabsTrigger value="analyzer"><Activity className="w-3 h-3 mr-1" />Analyzer</TabsTrigger>
          <TabsTrigger value="ab-test"><FlaskConical className="w-3 h-3 mr-1" />Testes A/B</TabsTrigger>
          <TabsTrigger value="chains">Cadeias</TabsTrigger>
        </TabsList>

        <TabsContent value="registry" className="space-y-4">
          <div className="flex flex-col md:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="buscar skill por nome ou descrição..." className="pl-8" />
            </div>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="active">Ativas</SelectItem>
                <SelectItem value="paused">Pausadas</SelectItem>
                <SelectItem value="training">Em treino (&lt;70)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-48" />)}
            </div>
          ) : filteredSkills.length === 0 ? (
            <SkillsEmptyState onCreate={openCreate} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredSkills.map((skill) => {
                const config = (skill.config ?? {}) as Record<string, unknown>;
                const meta = skillMeta[skill.name] || { icon: <Zap className="w-5 h-5" />, color: "text-muted-foreground" };
                const stats = getStats(skill.name);
                const quality = skill.quality_score ?? 0;

                return (
                  <Card key={skill.id} className={`hover:shadow-lg transition-all duration-300 ${!skill.active ? "opacity-60" : ""}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={meta.color}>{meta.icon}</span>
                          <CardTitle className="text-base">{skill.name}</CardTitle>
                        </div>
                        <Switch
                          checked={skill.active}
                          onCheckedChange={(checked) => handleToggle(skill.id, checked, quality)}
                          disabled={!skill.active && quality < 70}
                          title={!skill.active && quality < 70 ? `Bloqueado: quality ${quality}/100 < 70` : undefined}
                        />
                      </div>
                      <CardDescription className="text-xs">{skill.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex flex-wrap gap-1.5">
                        <Badge variant="outline" className="text-xs">{(config.task_class as string) || "balanced"}</Badge>
                        <Badge
                          variant={quality >= 85 ? "default" : quality >= 70 ? "secondary" : "destructive"}
                          className="text-xs"
                        >
                          {quality >= 85 ? "🟢 Pronta" : quality >= 70 ? "🟡 Beta" : "🔴 Em treino"}
                        </Badge>
                        <Badge variant="outline" className="text-xs">v{skill.version}</Badge>
                      </div>

                      {quality > 0 && (
                        <div>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-muted-foreground">Qualidade</span>
                            <span className={`font-medium ${quality >= 75 ? "text-emerald-500" : quality >= 50 ? "text-amber-500" : "text-destructive"}`}>
                              {quality}/100
                            </span>
                          </div>
                          <Progress value={quality} className="h-1.5" />
                        </div>
                      )}

                      {stats && (
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />{stats.count} exec</span>
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{Math.round(stats.avgMs)}ms</span>
                        </div>
                      )}

                      <div className="flex gap-1">
                        <SkillTestDialog skillName={skill.name} active={skill.active} />
                        <Link to={`/skills/${skill.id}`}>
                          <Button variant="outline" size="sm" title="Detalhes & Lab">
                            <Activity className="w-3 h-3" />
                          </Button>
                        </Link>
                        <Button variant="outline" size="sm" onClick={() => openEdit(skill)}><Pencil className="w-3 h-3" /></Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="analyzer">
          <SkillAnalyzerPanel />
        </TabsContent>

        <TabsContent value="ab-test">
          <ABTestPanel />
        </TabsContent>

        <TabsContent value="chains" className="space-y-4">
          <ChainBuilder />
          <ThresholdPlayground />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SkillsEmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <Card className="border-dashed">
      <CardContent className="py-12 flex flex-col items-center text-center">
        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <Sparkles className="w-7 h-7 text-primary" />
        </div>
        <h3 className="text-lg font-semibold mb-1">Crie seu primeiro skill personalizado</h3>
        <p className="text-sm text-muted-foreground max-w-md mb-6">
          Skills são habilidades reutilizáveis que a SYRA executa quando uma intent corresponde.
          Você pode criar skills hardcoded (lógica fixa) ou prompt-based (LLM com prompt customizado).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6 w-full max-w-2xl text-left">
          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="text-xs font-semibold mb-1">1. Defina a skill</div>
            <div className="text-xs text-muted-foreground">Nome, descrição, tipo de execução e prompt.</div>
          </div>
          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="text-xs font-semibold mb-1">2. Vincule intents</div>
            <div className="text-xs text-muted-foreground">A skill será roteada automaticamente.</div>
          </div>
          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="text-xs font-semibold mb-1">3. Teste e ative</div>
            <div className="text-xs text-muted-foreground">Use o preview integrado antes de ir ao ar.</div>
          </div>
        </div>
        <Button onClick={onCreate}><FlaskConical className="w-4 h-4 mr-1" /> Criar primeiro skill</Button>
      </CardContent>
    </Card>
  );
}

function SkillTestDialog({ skillName, active }: { skillName: string; active: boolean }) {
  const [input, setInput] = useState("");
  const [result, setResult] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [ms, setMs] = useState(0);

  const runTest = async () => {
    setLoading(true); setResult(null);
    const start = Date.now();
    try {
      const { data, error } = await supabase.functions.invoke("skill-executor", {
        body: { skill_name: skillName, context: { message: input }, test_mode: true },
      });
      setMs(Date.now() - start);
      if (error) setResult(`Erro: ${error.message}`);
      else setResult(data?.output || JSON.stringify(data));
    } catch (e) {
      setMs(Date.now() - start);
      setResult(`Erro: ${e instanceof Error ? e.message : String(e)}`);
    } finally { setLoading(false); }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-full" disabled={!active}>
          <Play className="w-3 h-3 mr-1" /> Testar
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Testar: {skillName}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Textarea placeholder="Digite uma mensagem de teste..." value={input} onChange={(e) => setInput(e.target.value)} rows={3} />
          <Button onClick={runTest} disabled={loading || !input.trim()} className="w-full">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
            Executar
          </Button>
          {result && (
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground"><Clock className="w-3 h-3" /> {ms}ms</div>
              <pre className="p-3 bg-muted rounded-md text-xs whitespace-pre-wrap max-h-60 overflow-auto">{result}</pre>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
